/**
 * Return path stashed across first-time onboarding.
 *
 * Some entry points sign a user in mid-task and must resume that task once the
 * user comes back — but a first-time signup has no org yet, so the task page
 * can't run: the wizard has to happen first. Those pages stash their own path
 * here, hand the user to /onboarding, and the wizard navigates back on commit.
 *
 * The stash is an allowlist, not a general redirector: only the entry points
 * below can round-trip, so a hostile `?redirect_url=` can never ride onboarding
 * to an arbitrary destination.
 */
const ONBOARDING_RETURN_KEY = 'soku.onboarding.returnPath'

// Entry points that may resume after onboarding. A path matches when it is the
// prefix exactly or the prefix followed by a query string — `/device-settings`
// must NOT inherit `/device`'s pass.
const RETURN_PATH_PREFIXES = [
  '/device',
  // The Connect funnel's own entry. Without it a visitor who signs up from the
  // Connect landing page has their destination dropped here and finishes
  // onboarding in the ordinary kickoff chat instead of Connect setup — the one
  // outcome that funnel exists to prevent.
  '/connect/start',
  '/connect/shopify',
  '/connect/shoplazza',
  '/connect/shopline',
  '/go/ad-library-remix',
  // AppSumo activation. A buyer who signs up from the claim page has no
  // workspace yet, so the page sends them through onboarding; without this
  // entry they finished it on the Free plan and the license stayed unbound.
  '/appsumo/activate',
] as const

function storage(): Storage | null {
  if (typeof window === 'undefined') return null
  return window.sessionStorage
}

export function onboardingReturnPathFromLocation(
  location: Pick<Location, 'pathname' | 'search'>,
): string | null {
  const path = `${location.pathname}${location.search ?? ''}`
  return sanitizeOnboardingReturnPath(path)
}

export function storeOnboardingReturnPath(path: string): void {
  const safePath = sanitizeOnboardingReturnPath(path)
  const store = storage()
  if (!safePath || !store) return
  store.setItem(ONBOARDING_RETURN_KEY, safePath)
}

export function consumeOnboardingReturnPath(): string | null {
  const store = storage()
  if (!store) return null
  const raw = store.getItem(ONBOARDING_RETURN_KEY)
  clearOnboardingReturnPath()
  return raw ? sanitizeOnboardingReturnPath(raw) : null
}

export function clearOnboardingReturnPath(): void {
  const store = storage()
  if (!store) return
  store.removeItem(ONBOARDING_RETURN_KEY)
}

function sanitizeOnboardingReturnPath(path: string): string | null {
  // `//host` is a protocol-relative absolute URL, not a same-origin path.
  if (path.startsWith('//')) return null
  const allowed = RETURN_PATH_PREFIXES.some(
    prefix => path === prefix || path.startsWith(`${prefix}?`),
  )
  return allowed ? path : null
}
